import axios from 'axios';
import { logger } from '../utils/logger';
import { installRequestId } from '../utils/requestId';
import { getAccountsApiOrigin } from './resolveApiBaseUrl';

/** Multipart uploads: never set Content-Type yourself — browser must add the boundary. */
const multipartRequestConfig = {
  headers: {},
  transformRequest: (data, headers) => {
    if (headers && typeof headers === 'object') {
      delete headers['Content-Type'];
      delete headers['content-type'];
    }
    return data;
  },
};

// Use environment variable for API URL, fallback to localhost for development
const API_BASE_URL = getAccountsApiOrigin();

// Create axios instance with credentials to send cookies
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests (httpOnly cookies)
});
installRequestId(api);

// Request interceptor - no longer need to add Authorization header
// Cookies are sent automatically by browser
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent by browser when withCredentials: true
    // No need to manually add Authorization header
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    logger.debug('API Response interceptor - Error:', error.response?.status);
    logger.debug('Original request URL:', originalRequest.url);

    const publicEndpoints = [
      '/api/login/',
      '/api/register/',
      '/api/password-reset/',
      '/api/password-reset/confirm/',
      '/api/otp/send/',
      '/api/otp/verify/',
      '/api/auth/request-otp/',
      '/api/check-user-exists/',
      '/api/logout/',
    ];
    const requestUrl = originalRequest?.url ?? '';
    const isPublicEndpoint = publicEndpoints.some((endpoint) => requestUrl.includes(endpoint));
    
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      logger.debug('401 error detected, attempting token refresh...');
      originalRequest._retry = true;
      
      try {
        // Refresh token is in httpOnly cookie, sent automatically
        await axios.post(
          `${API_BASE_URL}/api/token/refresh/`,
          {},  // No body needed - refresh token in cookie
          { withCredentials: true }  // Send cookies
        );
        
        logger.debug('Token refresh successful');
        // New tokens are set in httpOnly cookies by backend
        // No need to store in localStorage or read response
        
        // Retry original request (cookies will be sent automatically)
        return api(originalRequest);
      } catch (refreshError) {
        logger.error('Token refresh failed');
        // Only redirect to login if it's a persistent auth failure
        // Don't redirect on network errors or temporary issues
        if (
          !originalRequest.skipAuthRedirect
          && [400, 401, 403].includes(refreshError.response?.status)
        ) {
          // Redirect to login - cookies will be cleared by backend on logout
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Kept intentionally lenient (length only) to match the backend's Django
// validators — stricter client rules were silently blocking signups.
export const validatePassword = (password) => {
  const errors = [];
  if (!password || password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  return { isValid: errors.length === 0, errors };
};

export const PASSWORD_REQUIREMENT_HINTS = [
  'At least 8 characters long',
];

export const formatPasswordChangeError = (error) => {
  const data = error?.response?.data;
  if (data?.details) {
    const messages = (Array.isArray(data.details) ? data.details : [data.details])
      .flat()
      .map((item) => String(item))
      .filter(Boolean);
    if (messages.length) {
      return messages.join('\n');
    }
  }
  if (data?.error && data.error !== 'Password validation failed') {
    return data.error;
  }
  const message = (error?.message || '').trim();
  if (message) {
    return message.replace(/^Password validation failed:\s*/i, '').trim() || message;
  }
  return 'Failed to change password';
};

/**
 * Prefer nested API field details over wrappers like
 * { error: "Registration failed", details: { username: ["..."] } }.
 */
export const formatRegistrationError = (error, fallback = 'Registration failed') => {
  if (error == null || error === '') return fallback;
  if (typeof error === 'string') return error;

  const data = error?.response?.data || error;
  if (!data || typeof data !== 'object') {
    return error?.message || fallback;
  }

  if (data.details != null) {
    const details = data.details;
    if (typeof details === 'string' && details.trim()) return details;
    if (Array.isArray(details)) {
      const joined = details.map((item) => String(item)).filter(Boolean).join('\n');
      if (joined) return joined;
    }
    if (typeof details === 'object') {
      const messages = Object.entries(details).flatMap(([key, value]) => {
        const label = key.replace(/_/g, ' ');
        const values = Array.isArray(value) ? value : [value];
        return values
          .map((msg) => (msg == null || msg === '' ? '' : `${label}: ${msg}`))
          .filter(Boolean);
      });
      if (messages.length) return messages.join('\n');
    }
  }

  const genericWrappers = new Set([
    'Registration failed',
    'Please fix the highlighted fields and try again.',
  ]);
  if (typeof data.error === 'string' && data.error.trim() && !genericWrappers.has(data.error)) {
    return data.error;
  }
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail;

  const fieldMessages = Object.entries(data)
    .filter(([key]) => !['error', 'message', 'detail', 'details'].includes(key))
    .flatMap(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      return values
        .map((msg) => (msg == null || msg === '' ? '' : `${key.replace(/_/g, ' ')}: ${msg}`))
        .filter(Boolean);
    });
  if (fieldMessages.length) return fieldMessages.join('\n');

  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  return error?.message || fallback;
};

export const generateSecurePassword = () => {
  const lowers = 'abcdefghijklmnopqrstuvwxyz';
  const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const specials = '!@#$%^&*';
  const all = lowers + uppers + digits + specials;
  const pick = (pool) => pool.charAt(Math.floor(Math.random() * pool.length));
  const chars = [pick(lowers), pick(uppers), pick(digits), pick(specials)];
  while (chars.length < 16) {
    chars.push(pick(all));
  }
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

export const authAPI = {
  // Register new user
  register: async (userData) => {
    const response = await api.post('/api/register/', userData);
    return response.data;
  },

  // Login user
  login: async (credentials) => {
    logger.debug('authAPI: login called');
    logger.debug('authAPI: API_BASE_URL:', API_BASE_URL);
    try {
      // Drop any stale/inactive session cookies first so login is based on
      // the submitted credentials only (not a previous deactivated account).
      try {
        await api.post('/api/logout/', { platform: 'web' });
      } catch (_logoutError) {
        // Ignore — login must still proceed
      }

      const response = await api.post('/api/login/', credentials);
      logger.debug('authAPI: login response received');
      return response.data;
    } catch (error) {
      logger.error('authAPI: login error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL}${error.config?.url}`,
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.post('/api/logout/', { platform: 'web' });
    } catch (error) {
      logger.error('Logout API error:', error.response?.data || error.message);
    }
  },

  // Refresh token - refresh token is in httpOnly cookie, sent automatically
  refreshToken: async () => {
    const response = await api.post('/api/token/refresh/', {});
    return response.data;
  },

  // Get user profile
  getUserProfile: async ({ skipAuthRedirect = false } = {}) => {
    const response = await api.get('/api/user/', { skipAuthRedirect });
    return response.data;
  },

  /** Restore session on page load without forcing /login redirect when logged out. */
  restoreSession: async () => {
    try {
      const response = await api.get('/api/user/', { skipAuthRedirect: true });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      if ([400, 401, 403, 500, 502, 503].includes(status)) {
        return null;
      }
      throw error;
    }
  },

  // Lookup user by phone
  lookupUserByPhone: async (phoneNumber) => {
    const response = await api.get(`/api/user/${phoneNumber}/`);
    return response.data;
  },

  getUserByUsername: async (username) => {
    const response = await api.get(`/api/user-by-username/${encodeURIComponent(username)}/`);
    return response.data;
  },

  searchUsers: async (query) => {
    const response = await api.get('/api/users/search/', {
      params: { query }
    });
    return response.data;
  },

  getPublicProfile: async (username) => {
    const response = await api.get(`/api/profiles/${encodeURIComponent(username)}/`);
    return response.data;
  },

  followProfile: async (username) => {
    const response = await api.post(`/api/profiles/${encodeURIComponent(username)}/follow/`);
    return response.data;
  },

  unfollowProfile: async (username) => {
    const response = await api.delete(`/api/profiles/${encodeURIComponent(username)}/follow/`);
    return response.data;
  },

  blockProfile: async (username, reason = '') => {
    const response = await api.post(`/api/profiles/${encodeURIComponent(username)}/block/`, { reason });
    return response.data;
  },

  unblockProfile: async (username) => {
    const response = await api.delete(`/api/profiles/${encodeURIComponent(username)}/block/`);
    return response.data;
  },

  getFollowers: async (username) => {
    const response = await api.get(`/api/profiles/${encodeURIComponent(username)}/followers/`);
    return response.data;
  },

  getFollowing: async (username) => {
    const response = await api.get(`/api/profiles/${encodeURIComponent(username)}/following/`);
    return response.data;
  },

  // Test protected endpoint
  testProtectedEndpoint: async () => {
    const response = await api.get('/api/protected/');
    return response.data;
  },

  // Update user profile
  updateProfile: async (profileData) => {
    const response = await api.put('/api/user/', profileData);
    return response.data;
  },

  uploadProfilePicture: async (file) => {
    const formData = new FormData();
    formData.append('profile_picture', file);
    const response = await api.post('/api/user/profile-picture/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },

  removeProfilePicture: async () => {
    const response = await api.delete('/api/user/profile-picture/');
    return response.data;
  },

  // Change password
  changePassword: async (passwordData) => {
    logger.debug('authAPI.changePassword called');
    const passwordValidation = validatePassword(passwordData.new_password);
    if (!passwordValidation.isValid) {
      throw new Error(passwordValidation.errors.join('\n'));
    }
    try {
      const response = await api.post('/api/user/change-password/', passwordData);
      logger.debug('Password change API response received');
      return response.data;
    } catch (error) {
      logger.error('Password change API error');
      throw new Error(formatPasswordChangeError(error));
    }
  },

  deleteAccount: async ({ confirm, password }) => {
    try {
      const response = await api.delete('/api/user/', {
        data: { confirm, password },
      });
      return response.data;
    } catch (error) {
      logger.error('Delete account API error:', error.response?.data || error.message);
      throw error;
    }
  },
  
  // OTP functions
  sendOTP: async (phoneNumber, purpose = 'phone_verification', options = {}) => {
    try {
      const response = await api.post('/api/otp/send/', {
        phone_number: phoneNumber,
        purpose: purpose,
        ...(options.preferSms ? { prefer_sms: true } : {}),
      }, {
        timeout: 25000,
      });
      return response.data;
    } catch (error) {
      logger.error('Send OTP error:', error.response?.data || error.message);
      throw error;
    }
  },

  verifyOTP: async (phoneNumber, code, purpose = 'phone_verification') => {
    try {
      const response = await api.post('/api/otp/verify/', {
        phone_number: phoneNumber,
        code: code,
        purpose: purpose
      });
      return response.data;
    } catch (error) {
      logger.error('Verify OTP error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Check if user exists by email or phone number
  checkUserExists: async (email, phoneNumber) => {
    try {
      const response = await api.post('/api/check-user-exists/', {
        email: email || null,
        phone_number: phoneNumber || null
      });
      return response.data;
    } catch (error) {
      logger.error('Check user exists error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    try {
      const identifier = String(email || '').trim();
      const response = await api.post('/api/password-reset/', {
        identifier,
        email: identifier,
      });
      return response.data;
    } catch (error) {
      logger.error('Password reset request error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
      
      // If 405, it's a Cloudflare Tunnel routing issue
      if (error.response?.status === 405) {
        const errorMessage = 'Password reset is currently unavailable due to server configuration. Please contact support or try again later.';
        const enhancedError = new Error(errorMessage);
        enhancedError.status = 405;
        enhancedError.originalError = error;
        throw enhancedError;
      }
      
      // Provide user-friendly error messages
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        throw new Error('Network error: Cannot reach server. Please check your internet connection.');
      }
      
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      throw error;
    }
  },

  // Confirm password reset
  confirmPasswordResetWithOtp: async (identifier, code, newPassword) => {
    try {
      const response = await api.post('/api/password-reset/confirm/', {
        identifier,
        code,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      logger.error('Password reset OTP confirm error:', error.response?.data || error.message);
      throw error;
    }
  },

  confirmPasswordReset: async (uid, token, newPassword) => {
    try {
      const response = await api.post('/api/password-reset/confirm/', {
        uid,
        token,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      logger.error('Password reset confirm error:', error.response?.data || error.message);
      throw error;
    }
  },

  // User Interests API
  getInterests: async () => {
    const response = await api.get('/api/interests/');
    return response.data;
  },

  createInterest: async (interestData, imageFiles = []) => {
    if (imageFiles.length > 0) {
      const formData = new FormData();
      formData.append('item_name', interestData.item_name);
      if (interestData.category) formData.append('category', interestData.category);
      if (interestData.description) formData.append('description', interestData.description);
      imageFiles.forEach((file) => formData.append('images', file));
      const response = await api.post('/api/interests/', formData, multipartRequestConfig);
      return response.data;
    }
    const response = await api.post('/api/interests/', interestData);
    return response.data;
  },

  updateInterest: async (interestId, interestData, options = {}) => {
    const { imageFiles = [], removeImageIds = [] } = options;
    if (imageFiles.length > 0 || removeImageIds.length > 0) {
      const formData = new FormData();
      if (interestData.item_name !== undefined) formData.append('item_name', interestData.item_name);
      if (interestData.category !== undefined) formData.append('category', interestData.category || '');
      if (interestData.description !== undefined) formData.append('description', interestData.description || '');
      removeImageIds.forEach((id) => formData.append('remove_image_ids', String(id)));
      imageFiles.forEach((file) => formData.append('images', file));
      const response = await api.put(`/api/interests/${interestId}/`, formData, multipartRequestConfig);
      return response.data;
    }
    const response = await api.put(`/api/interests/${interestId}/`, interestData);
    return response.data;
  },

  deleteInterest: async (interestId) => {
    const response = await api.delete(`/api/interests/${interestId}/`);
    return response.data;
  },
};

export default authAPI;
